import React, { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useHandlerOrderContext } from "./providers/Handler";
import { useAppContext } from "../../AppProvider";
import {
  faArrowLeft,
  faArrowRight,
  faEdit,
  faEuro,
  faICursor,
  faInfoCircle,
  faLoader,
  faPercentage,
  faCheck,
  faEnvelope,
} from "@awesome.me/kit-c1b85ff10f/icons/classic/light";
import AsyncSelect from "../../Components/common/AsyncSelect/AsyncSelect";
import SyncSelect from "../../Components/Selects/SyncSelect/SyncSelect";
import Select from "../../Components/common/Selects/Select";
import {
  getAllCashOnDeliveryCollectionMethods,
  getAllWarehouses,
} from "../../utilities/asyncSelectCallAPI";
import { getWarehouseOptionStyle } from "../../Components/common/AsyncSelect/utilities";
import { useTypes } from "../../utilities/types";
import OrderDetails from "../OrderDetails";
import { IndexOrderDetailsProvider } from "../OrderDetails/providers/Index";
import {
  isDisabledConfirm,
  isDisabledDelete,
  isDisabledDeleteShipments,
  isDisabledDraft,
  isDisabledError,
  isDisabledProcess,
  isDisabledReturnDelivered,
} from "./utilities/list";
import { ButtonConfirm } from "../../Components/common/Buttons/ButtonConfirm";
import { ButtonDelete } from "../../Components/common/Buttons/ButtonDelete";
import { Modal, ModalBody, UncontrolledTooltip } from "reactstrap";
import { ConfirmOrdersProvider } from "./providers/Confirm";
import { DeleteOrdersProvider } from "./providers/Delete";
import { ProcessOrdersProvider } from "./providers/Process";
import Confirm from "./Confirm";
import Delete from "./Delete";
import Process from "./Process";
import Rate from "./Rate";
import moment from "moment";
import InputBoxIcon from "../../Components/common/InputBoxIcon";
import { DeleteShipmentsInOrdersProvider } from "./providers/DeleteShipment";
import DeleteShipments from "./DeleteShipments";
import InputBox from "../../Components/common/InputBox";
import { DraftOrdersProvider } from "./providers/Draft";
import Draft from "./Draft";
import { ReturnDeliveredOrdersProvider } from "./providers/ReturnDelivered";
import ReturnDelivered from "./ReturnDelivered";
import OrderEvents from "../OrderEvents";
import { IndexOrderEventsProvider } from "../OrderEvents/providers/Index";
import ButtonUtility from "../../Components/Buttons/Utility";
import { ShipmentOverviewProvider } from "../Shipments/providers/Overview";
import Overview from "../Shipments/Overview";
import { IndexEmailNotificationsProvider } from "../EmailNotifications/providers/Index";
import EmailNotifications from "../EmailNotifications";
import {
  calcTotalPriceVatIncluded,
  getGenericField,
  recalculateOrderValues,
} from "./utilities/index";
import { RejectOrderProvider } from "./providers/Reject";
import Reject from "./Reject";
import { AddressBookHandlerProvider } from "../AddressBook/providers";
import { AddressBookHandler } from "../AddressBook/AddressBookHandler";

const OrderHandler = () => {
  const action = window.location.pathname.split("/")[2];
  const { id } = useParams();

  const navigate = useNavigate();
  const types = useTypes();
  const { dictionary, accessTokenPayload } = useAppContext();

  let context = useHandlerOrderContext();

  let title = `${
    dictionary.words.new_sm
  } ${dictionary.orders.order.toLowerCase()}`;
  switch (action) {
    case "clone":
      title = `${
        dictionary.actions.duplicate
      } ${dictionary.orders.order.toLowerCase()}`;
      break;
    case "edit":
      title = `${
        dictionary.actions.edit
      } ${dictionary.orders.order.toLowerCase()}`;
      break;
    case "details":
      title = `${
        dictionary.actions.details
      } ${dictionary.orders.order.toLowerCase()}`;
      break;
  }

  const handleChange = (name, value, order = { ...context?.order }) => {
    if (action !== "details") {
      const newOrder = {
        ...order,
        [name]: value,
      };

      context?.editOrder(newOrder);
      context?.removeError(name);
    }
  };

  const renderOrderDetailsFooter = (data) => {
    const totalPriceVatIncluded = calcTotalPriceVatIncluded(data.content || []);

    const totalQuantity = data.content?.reduce(
      (t, { quantity }) => t + quantity,
      0,
    );
    return (
      <React.Fragment>
        <div
          className=" d-grid grid-column align-items-end ps-2"
          style={{
            gridTemplateColumns: `0.5fr 1.5fr 2.25fr 0.75fr 1fr 1fr 1fr 0.75fr 1.25fr 0.75fr 1.5fr`,
          }}
        >
          <div></div>
          <div></div>
          <div></div>
          <div>
            <label className="label-2">
              {dictionary.dimensions.total_quantity}:
            </label>
            <label className="label-3">{totalQuantity}</label>
          </div>
          <div></div>
          <div>
            <label className="label-2">
              {dictionary.dimensions.total_weight}:
            </label>
            <label className="label-3">
              {context?.order?.totalWeight
                ? `${context?.order?.totalWeight.toFixed(3)} kg`
                : "-"}
            </label>
          </div>
          <div></div>
          <div></div>
          <div>
            <label className="label-2">
              {dictionary.words.total_price} ({dictionary.words.vat}{" "}
              {dictionary.words.excluding_fs}):
            </label>
            <label className="label-3">
              {(context?.order?.totalPrice || 0)?.toFixed(2)} €
            </label>
          </div>
          <div></div>
          <div>
            <label className="label-2">
              {dictionary.words.total_price} ({dictionary.words.vat}{" "}
              {dictionary.words.including_fs})
            </label>
            <label className="label-3">
              {(totalPriceVatIncluded || 0)?.toFixed(2)} €
            </label>
          </div>
        </div>
        <hr></hr>
      </React.Fragment>
    );
  };

  useEffect(() => {
    if (action === "create") {
      context?.createOrder();
      return;
    } else if (action === "clone") {
      context?.cloneOrder();
      return;
    }
    context?.getOrder(id);
  }, []);

  const status = types.orderStatus.find(
    (s) => s.value === context?.order?.lastStatus,
  );
  const shipment = context?.order?.shipments?.[0]?.lastStatusName
    ? context?.order?.shipments[0]
    : null;
  const genericFields = context?.genericFields.map((gf) => {
    return { ...gf, jsonStructure: JSON.parse(gf.jsonStructure) };
  });

  return (
    <React.Fragment>
      {accessTokenPayload.owner === "7f8625b9-973b-4c66-80af-346b49cde2e7" && (
        <div className="row mb-2">
          <div className="col d-flex justify-content-end">
            {context?.order && !isDisabledConfirm([context?.order]) && (
              <ButtonConfirm
                className="me-1"
                onClick={() => {
                  context?.setModal({
                    open: true,
                    title: dictionary.actions.confirm,
                    action: "confirm",
                    data: [context?.order],
                    size: "sm",
                  });
                }}
              >
                {dictionary.words.return} {dictionary.words.to}{" "}
                {dictionary.actions.confirm}
              </ButtonConfirm>
            )}
            {context?.order && !isDisabledProcess([context?.order]) && (
              <ButtonConfirm
                className="me-1"
                onClick={() => {
                  context?.setModal({
                    open: true,
                    title: `${
                      dictionary.actions.process
                    } ${dictionary.orders.order.toLowerCase()}`,
                    action: "process",
                    data: [context?.order?.id],
                    size: "sm",
                  });
                }}
              >
                {dictionary.actions.process}
              </ButtonConfirm>
            )}
            {context?.order && !isDisabledError([context?.order]) && (
              <ButtonDelete
                className="mx-1"
                onClick={() => {
                  context?.setModal({
                    open: true,
                    title: dictionary.actions.reject,
                    action: "reject",
                    data: [context?.order.id],
                    size: "sm",
                  });
                }}
                icon={false}
              >
                {dictionary.actions.reject}
              </ButtonDelete>
            )}
            {context?.createShipmentEnabled && (
              <div className="button-1 button-1-dark justify-content-start p-0 ps-1">
                {!context.order.shipments?.[0] && (
                  <InputBox
                    name="parcelCount"
                    value={context.order.parcelCount || 1}
                    type="positive-integer-number"
                    onChange={({ target }) => {
                      context?.editOrder({
                        ...context?.order,
                        [target.name]: target.value,
                      });

                      handleChange(target.name, target.value);
                    }}
                    className={"w-25"}
                    min={1}
                    max={100}
                    required={true}
                  />
                )}
                <button
                  className="button-1 button-1-dark w-100"
                  onClick={() => {
                    context?.setModal({
                      open: true,
                      title: `${dictionary.words.new_sf} ${dictionary.shipments.shipment}`,
                      action: "createShipment",
                      order: context?.order,
                      size: "xl",
                    });
                  }}
                >
                  {context?.order.shipments
                    ? `${dictionary.actions.edit} ${dictionary.shipments.shipment}`
                    : `${dictionary.words.new_sf} ${dictionary.shipments.shipment}`}
                </button>
              </div>
            )}
            {context?.order && !isDisabledDeleteShipments([context?.order]) && (
              <ButtonDelete
                className="mx-1"
                onClick={() => {
                  context?.setModal({
                    open: true,
                    title: dictionary.actions.confirm_deletion,
                    action: "deleteShipment",
                    data: [context?.order],
                    size: "sm",
                  });
                }}
              >
                {dictionary.shipments.delete_shipment}
              </ButtonDelete>
            )}
          </div>
        </div>
      )}
      <div className="row align-items-center mb-2">
        <div className="col">
          <h2 className="m-0">{title}</h2>
        </div>
        <div className="col-3 text-end">
          <div
            style={{
              backgroundColor:
                context?.order.lastStatus === "SHIPPED" && shipment
                  ? shipment.lastStatusColor
                  : status?.color,
            }}
            className={`status w-auto`}
          >
            <span className={`name`}>
              {context?.order.lastStatus === "SHIPPED" && shipment
                ? types.shipmentStatus.find(
                    (s) => s.value === shipment.lastStatusCode,
                  )?.label
                : status?.label}
            </span>{" "}
            {context?.order?.lastStatus === "ERROR" && (
              <>
                <FontAwesomeIcon
                  id={"errorMessage_"}
                  className="cursor-pointer"
                  icon={faInfoCircle}
                />
                <UncontrolledTooltip
                  placement="bottom"
                  target={"errorMessage_"}
                >
                  {context?.order?.lastStatusDescription || "-"}
                </UncontrolledTooltip>
              </>
            )}
            {shipment?.deliveryDate && (
              <>
                <FontAwesomeIcon
                  id={"deliveryDate_"}
                  className="cursor-pointer"
                  icon={faInfoCircle}
                />{" "}
                <UncontrolledTooltip
                  placement="bottom"
                  target={"deliveryDate_"}
                >
                  {moment(shipment.deliveryDate).format("DD/MM/YYYY HH:mm")}
                </UncontrolledTooltip>
              </>
            )}
          </div>
          <span
            className="span-1 cursor-pointer text-decoration-underline"
            onClick={() => {
              context?.setModal({
                open: true,
                action: "events",
                data: [context?.order?.id],
                size: "md",
              });
            }}
          >
            {dictionary.messages.view_history}
          </span>
        </div>
      </div>
      <div className="row mb-2">
        <div className="col">
          <div className="box-container h-100 mb-2">
            {context?.orderLoader ? (
              <div className="row">
                <div className="col text-center">
                  <FontAwesomeIcon icon={faLoader} spin size="2x" />
                </div>
              </div>
            ) : (
              <React.Fragment>
                {context?.order.ownerId && (
                  <div className="text-center mb-2">
                    <img
                      style={{ maxWidth: "4rem", maxHeight: "3rem" }}
                      src={context?.order?.owner?.logoDarkUrl}
                    />
                    <label className="label-3">
                      {context?.order?.owner?.name} (
                      {context?.order?.owner?.externalReference || "-"})
                    </label>
                  </div>
                )}
                <h3 className="m-0">{dictionary.words.general_informations}</h3>
                <div
                  className="d-grid grid-column mt-2"
                  style={{
                    gridTemplateColumns: `1fr 1fr 1fr`,
                  }}
                >
                  <div>
                    <label className="label-2">
                      {dictionary.references.customer_reference}
                    </label>
                    <InputBoxIcon
                      name="externalReference"
                      value={context?.order?.externalReference || ""}
                      placeholder={action !== "details" ? "EXTREF123" : ""}
                      type="text"
                      onChange={(e) => {
                        handleChange(e.target.name, e.target.value);
                      }}
                      disabled={action === "details"}
                      error={
                        context?.orderError?.response?.data?.externalReference
                      }
                      icon={faICursor}
                    />
                  </div>
                  <div>
                    <label className="label-2">
                      {dictionary.references.logistic_reference}
                    </label>
                    <InputBoxIcon
                      name="logisticReference"
                      value={context?.order?.logisticReference || ""}
                      placeholder={action !== "details" ? "-" : ""}
                      type="text"
                      readOnly={true}
                      disabled={true}
                      icon={faICursor}
                    />
                  </div>
                  <div>
                    <label className="label-2">
                      {dictionary.references.allabox_reference}
                    </label>
                    <InputBoxIcon
                      name="internalReference"
                      value={context?.order?.internalReference || ""}
                      placeholder={action !== "details" ? "EXTREF123" : ""}
                      type="text"
                      readOnly={true}
                      disabled={true}
                      icon={faICursor}
                    />
                  </div>
                </div>
                <div
                  className="d-grid grid-column mt-1"
                  style={{
                    gridTemplateColumns: `1fr`,
                  }}
                >
                  <div>
                    <label className="label-2">
                      {dictionary.registries.warehouse}
                    </label>
                    <AsyncSelect
                      name="warehouse"
                      optionValue="id"
                      optionLabel="name"
                      value={context?.order?.warehouse}
                      onSearch={(selector, value) => {
                        handleChange(selector, value);
                      }}
                      loadOptions={(inputValue, pagination) => {
                        return getAllWarehouses(inputValue, pagination, {
                          orderBy: "asc",
                          selector: "name",
                        });
                      }}
                      optionStyle={getWarehouseOptionStyle}
                      isClearable={false}
                      isDisabled={action === "details"}
                      error={context?.orderError?.response?.data?.warehouse}
                      placeholder={action !== "details" ? "Select" : ""}
                    />
                  </div>
                </div>
                {genericFields?.length > 0 && (
                  <React.Fragment>
                    <h3 className="m-0 my-2">
                      {dictionary.words.additional_informations}
                    </h3>
                    <div className="row gx-2">
                      {genericFields.map((g, i) => {
                        const genericFieldInOrder =
                          context?.order?.genericFields?.find(
                            (gf) => gf.rank === g.rank,
                          );

                        const { type, options = [], label } = g.jsonStructure;

                        if (type !== "select") return null;

                        const enrichedOptions = options.map((o) => ({
                          ...o,
                          rank: g.rank,
                        }));

                        const selectedOption = enrichedOptions.find(
                          (o) => o.value === genericFieldInOrder?.value,
                        );

                        const handleChange = (name, option) => {
                          if (action !== "details") {
                            const updatedGenericFields =
                              context?.order?.genericFields?.map((field) =>
                                field.rank === option.rank ? option : field,
                              ) || [option];

                            let updatedOrder = {
                              ...context?.order,
                              genericFields: updatedGenericFields,
                            };
                            updatedOrder = recalculateOrderValues(
                              updatedOrder,
                              updatedOrder.orderDetails,
                              {
                                cashOnDelivery: option.cashOnDelivery,
                                insurance: option.insurance,
                              },
                            );

                            if (!option.cashOnDelivery) {
                              updatedOrder.cashOnDeliveryValue = null;
                            }
                            if (!option.insurance) {
                              updatedOrder.insuranceValue = null;
                            }

                            context?.editOrder(updatedOrder);
                          }
                        };

                        return (
                          <div className="col" key={i}>
                            <label className="label-2">{label}</label>
                            <SyncSelect
                              name={`${g.value}`}
                              value={selectedOption}
                              onChange={handleChange}
                              options={enrichedOptions}
                              isClearable={false}
                              isDisabled={action === "details"}
                              placeholder={
                                action !== "details"
                                  ? dictionary.actions.select
                                  : ""
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  </React.Fragment>
                )}
                <h3 className="m-0 mt-2">
                  {dictionary.shipments.shipment_informations}
                </h3>
                <div
                  className="d-grid grid-column align-items-end mt-2"
                  style={{
                    gridTemplateColumns: `1fr 1fr 1fr`,
                  }}
                >
                  <div>
                    <label className="label-2">
                      {dictionary.dimensions.goods_value} (
                      {dictionary.words.vat} {dictionary.words.excluding_fs}){" "}
                      <FontAwesomeIcon
                        id={"goodsValue_"}
                        className="cursor-pointer"
                        icon={faInfoCircle}
                      />
                    </label>
                    <UncontrolledTooltip
                      placement="bottom"
                      target={"goodsValue_"}
                    >
                      {dictionary.orders.calculated_logistics_goods_value}
                    </UncontrolledTooltip>
                    <InputBoxIcon
                      name="goodsValue"
                      value={context?.order.goodsValue || ""}
                      type="positive-number"
                      placeholder={action !== "details" ? "100.00" : ""}
                      onChange={(e) => {
                        handleChange(e.target.name, e.target.value);
                      }}
                      disabled={action === "details"}
                      icon={faEuro}
                      error={context?.orderError?.response?.data?.goodsValue}
                    />
                  </div>
                  <div>
                    <label className="label-2">
                      {dictionary.dimensions.insurance_value} (
                      {dictionary.words.vat} {dictionary.words.excluding_fs}){" "}
                      <FontAwesomeIcon
                        id={"insuranceValue_"}
                        className="cursor-pointer"
                        icon={faInfoCircle}
                      />
                    </label>
                    <UncontrolledTooltip
                      placement="bottom"
                      target={"insuranceValue_"}
                    >
                      {dictionary.orders.calculated_logistics_insurance_value}
                    </UncontrolledTooltip>
                    <InputBoxIcon
                      name="insuranceValue"
                      value={context?.order.insuranceValue || ""}
                      type="positive-number"
                      placeholder={action !== "details" ? "100.00" : ""}
                      onChange={(e) =>
                        handleChange(e.target.name, e.target.value)
                      }
                      disabled={action === "details"}
                      error={
                        context?.orderError?.response?.data?.insuranceValue
                      }
                      icon={faEuro}
                    />
                  </div>
                  <div>
                    <label className="label-2">
                      {dictionary.dimensions.cash_on_delivery_value} (
                      {dictionary.words.vat} {dictionary.words.including_fs}){" "}
                      <FontAwesomeIcon
                        id={"cash_on_delivery_"}
                        className="cursor-pointer"
                        icon={faInfoCircle}
                      />
                    </label>
                    <UncontrolledTooltip
                      placement="bottom"
                      target={"cash_on_delivery_"}
                    >
                      {
                        dictionary.orders
                          .calculated_logistics_cash_on_delivery_value
                      }
                    </UncontrolledTooltip>
                    <InputBoxIcon
                      name="cashOnDeliveryValue"
                      value={context?.order.cashOnDeliveryValue || ""}
                      type="positive-number"
                      placeholder={action !== "details" ? "100.00" : ""}
                      onChange={(e) =>
                        handleChange(e.target.name, e.target.value)
                      }
                      disabled={action === "details"}
                      error={
                        context?.orderError?.response?.data?.cashOnDeliveryValue
                      }
                      icon={faEuro}
                      min={1}
                      required={false}
                    />
                  </div>
                </div>
                <div
                  className="d-grid grid-column mt-1"
                  style={{
                    gridTemplateColumns: `1fr 1fr 1fr`,
                  }}
                >
                  <div>
                    <label className="label-2">
                      {dictionary.words.currency}
                    </label>
                    <Select
                      name="currency"
                      onChange={(option) => {
                        handleChange("currency", option);
                      }}
                      defaultMenuIsOpen={false}
                      isDisabled={action === "details"}
                      className="select"
                      options={types.currencies2}
                      value={context?.order?.currency}
                    />
                  </div>
                  {context?.order?.cashOnDeliveryValue > 0 && (
                    <div>
                      <label className="label-2">
                        {dictionary.words.cash_on_delivery_method}
                      </label>
                      <AsyncSelect
                        name="cashOnDeliveryCollectionMethodCode"
                        optionValue="code"
                        optionLabel="name"
                        value={
                          context?.order?.cashOnDeliveryCollectionMethodCode
                        }
                        onSearch={(selector, value) =>
                          handleChange(selector, value)
                        }
                        loadOptions={(inputValue, pagination) => {
                          return getAllCashOnDeliveryCollectionMethods(
                            inputValue,
                            pagination,
                            {
                              orderBy: "asc",
                              selector: "name",
                            },
                          );
                        }}
                        isDisabled={action === "details"}
                        isClearable={false}
                      />
                    </div>
                  )}
                </div>
                <div
                  className="d-grid grid-column mt-1"
                  style={{
                    gridTemplateColumns: `1fr`,
                  }}
                >
                  {context?.order?.shipments?.[0] && (
                    <div>
                      <label className="label-2">{dictionary.words.note}</label>
                      <InputBox
                        name="note"
                        value={context?.order?.shipments?.[0].note || ""}
                        placeholder={""}
                        type="textarea"
                        icon={faICursor}
                        disabled={action === "details"}
                      />
                    </div>
                  )}
                </div>

                <h3 className="m-0 mt-2">
                  {dictionary.words.billing_informations}
                </h3>
                <div
                  className="d-grid grid-column mt-1"
                  style={{
                    gridTemplateColumns: `1fr 1fr 1fr`,
                  }}
                >
                  <div>
                    <label className="label-2">
                      {dictionary.shipments.shipment_price} (
                      {dictionary.words.vat} {dictionary.words.excluding_fs})
                    </label>
                    <InputBoxIcon
                      name="shippingCost"
                      value={context?.order.shippingCost || ""}
                      type="positive-number"
                      placeholder={action !== "details" ? "100.00" : ""}
                      onChange={(e) => {
                        if (action !== "details") {
                          const { value, name } = e.target;
                          let newOrder = { ...context?.order, [name]: value };

                          const genericField = getGenericField(
                            context?.order.genericFields,
                            context?.genericFields,
                          );

                          newOrder = recalculateOrderValues(
                            newOrder,
                            newOrder.orderDetails,
                            {
                              cashOnDelivery:
                                genericField?.isCashOnDeliveryEnabled,
                              insuranceValue: genericField?.isInsuranceEnabled,
                            },
                          );

                          context?.editOrder(newOrder);
                          context?.removeError(name);
                        }
                      }}
                      disabled={action === "details"}
                      error={context?.orderError?.response?.data?.shippingCost}
                      icon={faEuro}
                    />
                  </div>
                  <div>
                    <label className="label-2">{dictionary.words.vat}</label>
                    <InputBoxIcon
                      name="vatRegime"
                      value={"22"}
                      type="text"
                      readOnly={true}
                      icon={faPercentage}
                      disabled={true}
                    />
                  </div>
                </div>
              </React.Fragment>
            )}
          </div>
        </div>
        <div className="col">
          <div className="box-container h-100 mb-2">
            <React.Fragment>
              <AddressBookHandlerProvider
                autosave={true}
                id={context?.order?.delivery?.id}
                label={dictionary.words.shipping_address}
                ownerId={accessTokenPayload.owner}
                callback={(addressBook) => {
                  const newOrder = { ...context?.order };
                  newOrder.delivery = addressBook;
                  newOrder.deliveryId = addressBook.id;
                  context?.editOrder(newOrder);
                }}
                service="logistic"
                action={action}
                registry={{ saveEnabled: true, searchEnabled: true }}
                billing={{
                  enabled: true,
                  addressBook: {
                    id: context?.order?.billing?.id,
                    service: "logistic",
                    enabled: true,
                    onChange: (addressBook) => {
                      if (action !== "details") {
                        const newOrder = { ...context?.order };
                        newOrder.billing = context?.order?.delivery;
                        newOrder.billingId = context?.order?.delivery.id;
                        if (addressBook) {
                          newOrder.billing = addressBook;
                          newOrder.billingId = addressBook.id;
                        }
                        context?.editOrder(newOrder);
                      }
                    },
                  },
                }}
              >
                <AddressBookHandler />
              </AddressBookHandlerProvider>
            </React.Fragment>
          </div>
        </div>
      </div>

      {context?.order?.id && (
        <IndexOrderDetailsProvider
          parentId={context?.order?.id}
          action={action}
        >
          <OrderDetails renderFooter={renderOrderDetailsFooter} />
        </IndexOrderDetailsProvider>
      )}

      <div className="row mt-3">
        <div className="col">
          {
            <Link to={`/orders`} className="button-1 button-1-transparent">
              <FontAwesomeIcon icon={faArrowLeft} /> {dictionary.words.back}
            </Link>
          }
        </div>
        <div className="col text-end align-items-center">
          {context?.order &&
            context?.order.lastStatus === "SHIPPED" &&
            context?.order.lastStatus === "RET_SENDER" &&
            context?.order.lastStatus === "RET_DELIVERED" && (
              <button
                className="button-1 button-1-transparent me-1"
                onClick={() => {
                  context?.setModal({
                    open: true,
                    title: `${dictionary.notifications.email_notifications}`,
                    action: "emailNotifications",
                    data: context?.order?.internalReference,
                    size: "lg",
                  });
                }}
              >
                <FontAwesomeIcon icon={faEnvelope} />
                {dictionary.notifications.email_notifications}
              </button>
            )}
          {context?.order && !isDisabledDraft([context?.order]) && (
            <button
              className="button-1 button-1-transparent me-1"
              onClick={() => {
                context?.setModal({
                  open: true,
                  title: `${dictionary.orders.edit_order}`,
                  action: "draft",
                  data: [context?.order?.id],
                  size: "sm",
                });
              }}
            >
              <FontAwesomeIcon icon={faEdit} />
              {dictionary.actions.edit}
            </button>
          )}
          {context?.order && !isDisabledReturnDelivered([context?.order]) && (
            <button
              className="button-1 button-1-transparent me-1"
              onClick={() => {
                context?.setModal({
                  open: true,
                  title: `${dictionary.status.return_delivered}`,
                  action: "returnDelivered",
                  data: [context?.order],
                  size: "sm",
                });
              }}
            >
              <FontAwesomeIcon icon={faCheck} />
              {dictionary.status.return_delivered}
            </button>
          )}
          {context?.order && !isDisabledConfirm([context?.order]) && (
            <ButtonConfirm
              className="me-1"
              onClick={() => {
                context?.setModal({
                  open: true,
                  title: dictionary.actions.confirm,
                  action: "confirm",
                  data: [context?.order],
                  size: "sm",
                });
              }}
            >
              {dictionary.actions.confirm}
            </ButtonConfirm>
          )}
          {context?.order && !isDisabledDelete([context?.order]) && (
            <ButtonDelete
              className="ms-1"
              onClick={() => {
                context?.setModal({
                  open: true,
                  title: dictionary.actions.confirm_deletion,
                  action: "delete",
                  data: [context?.order],
                  size: "sm",
                });
              }}
            />
          )}
          {context?.order?.shipments?.[0] && (
            <Link
              target="_blank"
              className={`button-1 button-1-dark ms-1`}
              to={"/shipments/details/" + context?.order?.shipments?.[0].id}
            >
              {dictionary.shipments.go_to_shipment}
              <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          )}
        </div>
      </div>

      <Modal isOpen={context?.modal.open} size={context?.modal.size}>
        <ModalBody>
          {context?.modal.action === "shipmentOverview" && (
            <ShipmentOverviewProvider id={context?.modal.data}>
              <Overview />
              <div className="d-flex mt-3">
                <ButtonUtility
                  className="w-50 me-2"
                  onClick={() => {
                    context?.getOrder();
                    context.setModal({ open: false });
                  }}
                >
                  {dictionary.actions.cancel}
                </ButtonUtility>
                <ButtonConfirm
                  className="w-50"
                  onClick={context?.processNextOrder}
                >
                  {dictionary.actions.process}{" "}
                  {dictionary.words.next_sm.toLowerCase()}{" "}
                  {dictionary.orders.order.toLowerCase()}
                </ButtonConfirm>
              </div>
            </ShipmentOverviewProvider>
          )}
          {context?.modal.action === "emailNotifications" && (
            <IndexEmailNotificationsProvider
              parentId={context?.modal.data}
              action="details"
              callback={() => {
                context?.getOrder();
                context.setModal({ open: false });
              }}
            >
              <h3 className=" text-center">{context?.modal.title}</h3>
              <EmailNotifications />
              <ButtonUtility
                className="mt-2"
                onClick={() => {
                  context?.getOrder();
                  context.setModal({ open: false });
                }}
              >
                {dictionary.actions.cancel}
              </ButtonUtility>
            </IndexEmailNotificationsProvider>
          )}
          {context?.modal.action === "confirm" && (
            <ConfirmOrdersProvider
              data={context?.modal.data}
              callback={(response) => {
                if (response) {
                  context?.getOrder();
                }
                context.setModal({ open: false });
              }}
            >
              <Confirm />
            </ConfirmOrdersProvider>
          )}
          {context?.modal.action === "draft" && (
            <DraftOrdersProvider
              data={context?.modal.data}
              callback={(response) => {
                if (response) {
                  navigate("/orders/edit/" + id, { replace: true });
                }
                context.setModal({ open: false });
              }}
            >
              <Draft />
            </DraftOrdersProvider>
          )}
          {context?.modal.action === "process" && (
            <ProcessOrdersProvider
              data={context?.modal.data}
              callback={(response) => {
                if (response) {
                  context?.getOrder();
                }
                context.setModal({ open: false });
              }}
            >
              <Process />
            </ProcessOrdersProvider>
          )}
          {context?.modal.action === "returnDelivered" && (
            <ReturnDeliveredOrdersProvider
              data={context?.modal.data}
              callback={(response) => {
                if (response) {
                  context?.getOrder();
                }
                context.setModal({ open: false });
              }}
            >
              <ReturnDelivered />
            </ReturnDeliveredOrdersProvider>
          )}
          {context?.modal.action === "delete" && (
            <DeleteOrdersProvider
              data={context?.modal.data}
              callback={(response) => {
                if (response) {
                  navigate("/orders", { replace: true });
                }
                context.setModal({ open: false });
              }}
            >
              <Delete />
            </DeleteOrdersProvider>
          )}

          {context?.modal.action === "reject" && (
            <RejectOrderProvider
              data={context?.modal.data}
              callback={() => {
                context?.getOrder();
                context.setModal({ open: false });
              }}
            >
              <Reject />
            </RejectOrderProvider>
          )}

          {context?.modal.action === "createShipment" && (
            <Rate
              callback={(shipmentId) => {
                if (shipmentId) {
                  context?.setCreateShipmentEnabled(false);
                  context?.setModal({
                    open: true,
                    title: `${dictionary.words.overview} ${dictionary.shipments.shipment}`,
                    action: "shipmentOverview",
                    data: shipmentId,
                    size: "xl",
                  });
                  return;
                }
                context?.getOrder();
                context.setModal({ open: false });
              }}
            />
          )}
          {context?.modal.action === "deleteShipment" && (
            <DeleteShipmentsInOrdersProvider
              data={context?.modal.data}
              callback={(response) => {
                if (response) {
                  window.location.href = "/orders/details/" + id;
                }
                context.setModal({ open: false });
              }}
            >
              <DeleteShipments />
            </DeleteShipmentsInOrdersProvider>
          )}
          {context?.modal.action === "events" && (
            <IndexOrderEventsProvider parentId={context?.modal.data[0]}>
              <OrderEvents />
              <ButtonUtility
                className="mt-2"
                onClick={() => {
                  context?.setModal({ open: false });
                  context?.getOrder();
                }}
              >
                {dictionary.actions.cancel}
              </ButtonUtility>
            </IndexOrderEventsProvider>
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default OrderHandler;
